import { useEffect, useRef, useState } from "react";
import chevronRightButton from "../../assets/Fonts/chevron-right-button.svg";
import { useStaffMode } from "../../hooks/StaffModeProvider";

export default function ScrollUpFloatingButton() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [shouldShow, setShouldShow] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const lastShowStateRef = useRef<boolean>(false);
  const lastPositionRef = useRef<string>("");
  const { isStaffMode } = useStaffMode();

  useEffect(() => {
    const findScrollContainer = (): HTMLElement | null => {
      const footer = document.getElementById("main-footer");
      if (footer) {
        let element: HTMLElement | null = footer.parentElement;
        while (element && element !== document.documentElement) {
          const style = window.getComputedStyle(element);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;
            if (scrollHeight > clientHeight) {
              return element;
            }
          }
          element = element.parentElement;
        }
      }
      
      const contentContainer = document.getElementById("content-container");
      if (contentContainer) {
        const containerHeight = contentContainer.scrollHeight;
        const containerClientHeight = contentContainer.clientHeight;
        const scrollableHeight = containerHeight - containerClientHeight;
        const minScrollHeight = window.innerHeight * 0.015;
        
        if (scrollableHeight > minScrollHeight) {
          return contentContainer;
        }
      }
      
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      if (documentHeight > windowHeight) {
        return null;
      }
      
      return null;
    };

    const checkScrollRequirements = (scrollContainer: HTMLElement | null) => {
      const windowHeight = window.innerHeight;
      
      if (scrollContainer) {
        const containerHeight = scrollContainer.scrollHeight;
        const scrollTop = scrollContainer.scrollTop;
        const containerClientHeight = scrollContainer.clientHeight || windowHeight;
        const scrollableHeight = containerHeight - containerClientHeight;
        const minScrollHeight = windowHeight * 0.015;

        if (scrollableHeight <= minScrollHeight) {
          return false;
        }

        const halfScreen = windowHeight * 0.5;
        return scrollTop >= halfScreen;
      }
      
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollableHeight = documentHeight - windowHeight;
      const minScrollHeight = windowHeight * 0.015;

      if (scrollableHeight <= minScrollHeight) {
        return false;
      }

      const halfScreen = windowHeight * 0.5;
      return windowScrollTop >= halfScreen;
    };

    const updateLoop = () => {
      const scrollContainer = findScrollContainer();
      const footer = document.getElementById("main-footer");
      const button = buttonRef.current;

      if (!footer || !button) {
        animationFrameRef.current = requestAnimationFrame(updateLoop);
        return;
      }

      const show = checkScrollRequirements(scrollContainer);
      if (show !== lastShowStateRef.current) {
        lastShowStateRef.current = show;
        setShouldShow(show);
      }

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const isFooterVisible =
        footerRect.top < windowHeight && footerRect.bottom > 0;

      if (isFooterVisible) {
        const distanceFromBottom = windowHeight - footerRect.top;
        const gap = 0;
        const bottomPosition = distanceFromBottom + gap;

        const newPosition = `fixed|${bottomPosition}px`;

        if (newPosition !== lastPositionRef.current) {
          lastPositionRef.current = newPosition;
          button.style.position = "fixed";
          button.style.bottom = `${bottomPosition}px`;
          button.style.top = "auto";
        }
      } else {
        const newPosition = "fixed|0px";

        if (newPosition !== lastPositionRef.current) {
          lastPositionRef.current = newPosition;
          button.style.position = "fixed";
          button.style.bottom = "0px";
          button.style.top = "auto";
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateLoop);
    };

    animationFrameRef.current = requestAnimationFrame(updateLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={buttonRef}
      className={`right-6 md:right-8 lg:right-12 z-[9999] transition-opacity duration-300 ${
        shouldShow
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
      style={{
        position: "fixed",
        bottom: "0px",
      }}
    >
      <button
        className={`relative overflow-hidden ${
          isStaffMode ? "bg-secondary" : "bg-primary"
        } text-white w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 shadow-lg flex items-center justify-center group`}
        onClick={() => {
          let scrollTarget: HTMLElement | null = null;
          
          const footer = document.getElementById("main-footer");
          if (footer) {
            let element: HTMLElement | null = footer.parentElement;
            while (element && element !== document.documentElement) {
              const style = window.getComputedStyle(element);
              if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                const scrollHeight = element.scrollHeight;
                const clientHeight = element.clientHeight;
                if (scrollHeight > clientHeight) {
                  scrollTarget = element;
                  break;
                }
              }
              element = element.parentElement;
            }
          }
          
          if (!scrollTarget) {
            const contentContainer = document.getElementById("content-container");
            if (contentContainer) {
              const containerHeight = contentContainer.scrollHeight;
              const containerClientHeight = contentContainer.clientHeight;
              const scrollableHeight = containerHeight - containerClientHeight;
              const minScrollHeight = window.innerHeight * 0.015;
              
              if (scrollableHeight > minScrollHeight) {
                scrollTarget = contentContainer;
              }
            }
          }
          
          if (scrollTarget) {
            scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
      >
        <span
          className={`absolute inset-0 ${
            isStaffMode ? "bg-secondary-dark" : "bg-primary-dark"
          } translate-y-full group-hover:translate-y-0 transition-transform duration-[400ms] ease-in-out`}
          style={{ zIndex: 0 }}
        />
        <img
          src={chevronRightButton}
          alt="chevron up"
          className="w-6 h-6 -rotate-90 relative z-10"
        />
      </button>
    </div>
  );
}
